import React, { useState, useRef } from "react";
import { TextField, InputAdornment, IconButton, Popover, Box, Tooltip, useMediaQuery } from "@mui/material";
import { InsertEmoticon, AttachFile, Mic, Stop, FormatBold, Send, AutoAwesome } from "@mui/icons-material";
import ChannelSelector from "./ChannelSelector";

const MessageSMSChannel = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  formatOptions,
  isRecording,
  startRecording,
  stopRecording,
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  audioURL,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
}) => {
  const [formatAnchorEl, setFormatAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)"); // Detecta se está no mobile
  const inputBorderRadius = 16;
  const canUseAiSuggestion =
    !!onGenerateSuggestion && channelType !== "Xpro" && hasHistory;
  const aiDisabled = isGeneratingSuggestion || !hasHistory;
  const hasFormatOptions = !!formatOptions?.[channelType]?.length;

  const handleFormatPickerOpen = (event) => setFormatAnchorEl(event.currentTarget);
  const handleFormatPickerClose = () => setFormatAnchorEl(null);

  const handleApplyFormat = (format) => {
    const input = inputRef.current;
    if (input) {
      const [startTag, endTag] = format.split("texto");
      const { selectionStart, selectionEnd } = input;

      if (selectionStart !== null && selectionEnd !== null) {
        const beforeText = message.substring(0, selectionStart);
        const selectedText = message.substring(selectionStart, selectionEnd);
        const afterText = message.substring(selectionEnd);

        setMessage(`${beforeText}${startTag}${selectedText}${endTag}${afterText}`);
      }
    }
    handleFormatPickerClose();
  };

  return (
    <Box
      sx={{
        position: "relative", 
        width: "100%",
        display: "flex",
        flexDirection: "column",
        paddingBottom: 0,
      }}
    >
      <TextField
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        variant="outlined"
        placeholder={channelType === "Xpro" ? `Registrar uma anotação` : `Envie uma mensagem via ${channelType}`}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        InputProps={{
          style: { borderRadius: `${inputBorderRadius}px`, fontSize: "1rem" },
          inputRef: inputRef,
          endAdornment: !isMobile && ( // Exibe botões ao lado apenas no desktop
            <InputAdornment position="end">
              {canUseAiSuggestion && (
                <Tooltip
                  title={
                    !hasHistory
                      ? "Disponível após a conversa ter histórico."
                      : isGeneratingSuggestion
                      ? "Gerando resposta..."
                      : "Sugerir resposta com IA"
                  }
                >
                  <span>
                    <IconButton
                      onClick={onGenerateSuggestion}
                      disabled={aiDisabled}
                    >
                      <AutoAwesome />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <IconButton onClick={handleEmojiPickerOpen}>
                <InsertEmoticon />
              </IconButton>
            {hasFormatOptions && (
              <IconButton onClick={handleFormatPickerOpen}>
                <FormatBold />
              </IconButton>
            )}
              <IconButton onClick={openFilePicker}>
                <AttachFile />
              </IconButton>
              <IconButton onClick={isRecording ? stopRecording : startRecording}>
                {isRecording ? <Stop /> : <Mic />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiInputBase-root": {
            borderRadius: `${inputBorderRadius}px !important`,
            overflow: "hidden",
            clipPath: `inset(0 round ${inputBorderRadius}px)`,
            "&::-webkit-scrollbar": {
              width: "5px", // Largura da barra de rolagem
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#b0b0b0", // Cor da barra
              borderRadius: "10px", // Arredondamento
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#888888", // Cor da barra ao passar o mouse
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1", // Cor do fundo do track
            },
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: `${inputBorderRadius}px !important`,
            overflow: "hidden",
            clipPath: `inset(0 round ${inputBorderRadius}px)`,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: `${inputBorderRadius}px !important`,
          },
          "& textarea": {
            overflowY: "auto",
            resize: "none",
            boxSizing: "border-box",
            borderRadius: "inherit",
            overflowX: "hidden",
            padding: "4px 14px",
            "&::-webkit-scrollbar": {
              width: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#b0b0b0",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#888888",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f1f1",
            },
          },
        }}
      />
      {isMobile && (
        <Box
          // Ajusta a posição dos botões no mobile
          sx={{
            display: "flex",
            justifyContent: "space-between", // Distribui os botões horizontalmente
            alignItems: "center",
            padding: "8px",
            gap: "8px",
            mt: 0.5,
            position: "relative",
            width: "100%",
            zIndex: 1,
          }}
        >
          <ChannelSelector selectedChannel={selectedChannel} setSelectedChannel={setSelectedChannel} />
          {canUseAiSuggestion && (
            <IconButton
              onClick={onGenerateSuggestion}
              disabled={aiDisabled}
            >
              <AutoAwesome />
            </IconButton>
          )}
          <IconButton onClick={handleEmojiPickerOpen}>
            <InsertEmoticon />
          </IconButton>
          <IconButton
            onClick={handleSendMessage}
            disabled={!message.trim() && !file && !audioURL}
            sx={{
              backgroundColor: "#3f51b5",
              color: "#fff",
              borderRadius: "50%",
              padding: "8px",
              "&:hover": { backgroundColor: "#303f9f" },
            }}
          >
            <Send />
          </IconButton>
        </Box>
      )}
      <Popover
        open={Boolean(formatAnchorEl)}
        anchorEl={formatAnchorEl}
        onClose={handleFormatPickerClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box p={2} display="flex" gap={1}>
          {hasFormatOptions && formatOptions[channelType]?.map((format, index) => (
            <Tooltip key={index} title={format.tooltip}>
              <IconButton onClick={() => handleApplyFormat(format.action)}>
                {format.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default MessageSMSChannel;
